import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { MailVoorkeuren } from "@/components/MailVoorkeuren";
import { Mail, CheckCircle2, XCircle, AlertTriangle, Clock, Send } from "@/components/icons";

// Owner-only e-mail panel: shows whether mail is live and lets the owner send
// ONE real test mail to their own address through the exact P1 path. The proof
// belongs in production (where the managed Resend key lives); in preview this
// will honestly report "not configured".
export const SettingsEmail = () => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null); // last test outcome
  // P1 / §5.5: the P1 verdict is reported by MailVoorkeuren but SAID here, at
  // the top of the screen, above the status card and the test-mail result.
  const [p1BereiktNiemand, setP1BereiktNiemand] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/email");
      setState(data);
    } catch (e) {
      toast.error(getErrorMessage(e, "Kan e-mailstatus niet laden."));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const sendTest = async () => {
    setSending(true);
    setResult(null);
    try {
      const { data } = await api.post("/settings/email/test");
      setResult(data);
      if (data.status === "sent") toast.success("Testmail geaccepteerd door Resend.");
      else if (data.status === "not_configured") toast.info("E-mail is niet geconfigureerd.");
      else toast.error("Resend weigerde de testmail.");
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, "Testmail versturen mislukt."));
    } finally {
      setSending(false);
    }
  };

  const fmt = (iso) => {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      return d.toLocaleString("nl-NL", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  if (loading) return <p className="text-body text-muted-foreground">Laden...</p>;

  const configured = !!state?.configured;
  const last = state?.last_attempt;

  return (
    <div className="space-y-4" data-testid="settings-email">
      {p1BereiktNiemand && (
        <div className="border border-letop-rand bg-letop-vlak px-3 py-2.5 text-body text-letop-tekst" data-testid="mail-p1-uit-banner">
          Niemand krijgt een mail bij een P1-storing. Het belletje in de app blijft werken.
        </div>
      )}
      <Card className="p-5">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 rounded-md bg-muted p-2">
            <Mail className="h-5 w-5 text-foreground" aria-hidden="true" />
          </div>
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-body font-semibold text-foreground">E-mailbezorging</h2>
              {configured ? (
                <Badge data-testid="email-status-badge" className="border-goed-rand bg-goed-vlak text-goed-tekst">
                  Actief
                </Badge>
              ) : (
                <Badge data-testid="email-status-badge" variant="secondary">
                  Niet geconfigureerd
                </Badge>
              )}
            </div>
            <p className="mt-1 max-w-2xl text-body text-muted-foreground">
              P1-storingen sturen automatisch een melding naar de TD en de eigenaar. Hieronder zie je
              of dat op dit moment echt verstuurd wordt, en kun je het testen.
            </p>

            {configured && (
              <div
                className="mt-3 rounded-md border border-goed-rand bg-goed-vlak px-3 py-2.5 "
                data-testid="email-active-sender"
              >
                <div className="text-data font-medium text-goed-tekst ">
                  Actieve afzender
                </div>
                <div className="mt-0.5 font-mono text-body font-semibold text-foreground" data-testid="email-active-sender-value">
                  {state?.sender}
                </div>
                <p className="mt-1 text-data text-muted-foreground">
                  Mail wordt vanaf dit adres verstuurd. Wijzig je dit naar een adres op een niet-geverifieerd
                  domein, dan weigert Resend de mail — verstuur daarna een testmail om het te controleren.
                </p>
              </div>
            )}

            <div className="mt-4 grid gap-x-8 gap-y-2 text-body sm:grid-cols-2">
              <div className="flex justify-between gap-4 border-b border-border py-1.5">
                <span className="text-muted-foreground">Aanbieder</span>
                <span className="font-medium text-foreground" data-testid="email-provider">Resend</span>
              </div>
              <div className="flex justify-between gap-4 border-b border-border py-1.5">
                <span className="text-muted-foreground">Afzender</span>
                <span className="font-medium text-foreground" data-testid="email-sender">{state?.sender}</span>
              </div>
              <div className="flex justify-between gap-4 border-b border-border py-1.5">
                <span className="text-muted-foreground">Automatische taken</span>
                <span className="font-medium text-foreground" data-testid="email-scheduler">
                  {state?.scheduler_enabled ? "Aan" : "Uit"}
                </span>
              </div>
              <div className="flex justify-between gap-4 border-b border-border py-1.5">
                <span className="text-muted-foreground">Laatste poging</span>
                <span className="flex items-center gap-1.5 font-medium text-foreground" data-testid="email-last-attempt">
                  {last ? (
                    <>
                      <Clock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                      {fmt(last.created_at)}{" "}
                      {last.sent ? (
                        <span className="text-goed-tekst">(verstuurd)</span>
                      ) : last.reden === "uitgezet" ? (
                        <span className="text-muted-foreground" data-testid="email-last-attempt-uitgezet">(mail uitgezet)</span>
                      ) : (
                        <span className="text-muted-foreground">(gelogd)</span>
                      )}
                    </>
                  ) : (
                    "-"
                  )}
                </span>
              </div>
            </div>

            {!configured && (
              <p className="mt-3 text-data text-muted-foreground">
                In deze preview-omgeving is er geen e-mailsleutel, dus meldingen worden alleen gelogd en in de app
                bewaard. In productie (waar de Resend-integratie is gekoppeld) wordt echt verstuurd.
              </p>
            )}

            <Separator className="my-4" />

            <div className="flex flex-wrap items-center gap-3">
              <Button data-testid="send-test-email-button" onClick={sendTest} disabled={sending}>
                <Send className="mr-2 h-4 w-4" aria-hidden="true" />
                {sending ? "Versturen..." : "Verstuur testmail"}
              </Button>
              <span className="text-data text-muted-foreground">
                Stuurt één testmail naar je eigen adres via dezelfde route als een P1-melding. Max. 5 per uur.
              </span>
            </div>
          </div>
        </div>
      </Card>

      {result && (
        <div data-testid="email-test-result">
          {result.status === "sent" && (
            <Alert className="border-goed-rand bg-goed-vlak ">
              <CheckCircle2 className="h-4 w-4 text-goed-tekst" aria-hidden="true" />
              <AlertTitle>Testmail geaccepteerd door Resend</AlertTitle>
              <AlertDescription>
                Verstuurd naar <b>{result.to}</b> vanaf <b>{result.sender}</b>.
                {result.message_id ? (
                  <> Bericht-id: <code className="text-data">{result.message_id}</code>.</>
                ) : null}{" "}
                Controleer je inbox (en eventueel de spammap).
              </AlertDescription>
            </Alert>
          )}
          {result.status === "failed" && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" aria-hidden="true" />
              <AlertTitle>Resend weigerde de testmail</AlertTitle>
              <AlertDescription>
                {result.hint ? (
                  <p className="mb-1 font-medium">{result.hint}</p>
                ) : null}
                <p className="text-data opacity-90">
                  Afzender: {result.sender}. Foutmelding van Resend: {result.error}
                </p>
              </AlertDescription>
            </Alert>
          )}
          {result.status === "not_configured" && (
            <Alert className="border-letop-rand bg-letop-vlak ">
              <AlertTriangle className="h-4 w-4 text-letop-tekst" aria-hidden="true" />
              <AlertTitle>E-mail is niet geconfigureerd</AlertTitle>
              <AlertDescription>
                Er is geen Resend-sleutel actief in deze omgeving, dus er is niets verstuurd. De melding is wel
                in de app bewaard. Koppel de Resend-integratie in productie om echt te versturen.
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}

      <MailVoorkeuren onP1Status={setP1BereiktNiemand} />
    </div>
  );
};
